import React, { useState } from 'react';
import styles from '../RecentOrders/RecentOrderList/recentOrderList.module.scss';
import { useNavigate } from 'react-router';
import { CUSTOMER_MICRO_SERVICE, ROUTE_PROFILE_DETAILS } from 'src/constants';
import useGetAuthenticatedDevices from 'src/react-query-hooks/AuthenticatedDevices/useGetAuthenticatedDevices';
import useRemoveAllDevices from 'src/react-query-hooks/AuthenticatedDevices/useRemoveAllDevices';
import useRemoveDevice from 'src/react-query-hooks/AuthenticatedDevices/useRemoveDevice';
import AuthenticatedDeviceCard from './authenticatedDeviceCard';
import { useAppDispatch } from 'src/redux/store/store';
import { unauthorizeLogout } from 'src/Features/AccountSetup/redux/actions';
import { Toast_Func } from 'src/helper/toast.helper';
import CustomModal from 'src/components/customModal/CustomModal';
import ConfirmationModal from 'src/components/ConfirmationModal/ConfirmationModal';
import { useQueryClient } from 'react-query';
import Loader from 'src/components/Loader/Loader';
import axiosWithInterceptor from 'src/services/axiosWithInterceptor';
import OtpVerificationModal from 'src/containers/account/OtpVeficationModal';

const MoreDevices: React.FC = () => {
  const navigate = useNavigate();
  const goToRoute = (route: string) => navigate(route);
  const { data: allDevices, isLoading } = useGetAuthenticatedDevices();
  const { mutateAsync: deleteDevices, isLoading: removingDevices } =
    useRemoveAllDevices();
  const { mutateAsync: deleteDevice, isLoading: removingDevice } =
    useRemoveDevice();
  const dispatch = useAppDispatch();
  const [showConfirmationModal, setShowConfirmationModal] =
    useState<boolean>(false);
  const [confirmationModalText, setConfirmationModalText] =
    useState<string>('');
  const [deviceToken, setDeviceToken] = useState<string>(null);
  const queryClient = useQueryClient();
  const [displayOtpModal, setDisplayOtpModal] = useState<boolean>(false);
  const [otpExpiryTime, setOtpExpiryTime] = useState<string>(null);
  const [isSendingOtp, setIsSendingOtp] = useState<boolean>(false);
  const [email, setEmail] = useState<string>(null);
  const [otpModalDescription, setOtpModalDescription] = useState<string>(null);

  const hideConfirmationModal = () => {
    setShowConfirmationModal(false);
    setDeviceToken(null);
  };

  const removeAllDevices = async (values, setSubmitting) => {
    setSubmitting(true);
    deleteDevices({ otp_code: values.otp })
      .then((data) => {
        hideConfirmationModal();
        unauthorizeLogout(dispatch);
        navigate('/');
        Toast_Func({
          status: true,
          message: 'Devices removed successfully.',
        });
      })
      .catch((error) => {
        Toast_Func({
          status: false,
          message: error.response.data.message,
        });
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const removeSingleDevice = () => {
    deleteDevice(deviceToken)
      .then((data) => {
        let deletedDevice = allDevices.find(
          (device) => device.token === deviceToken,
        );
        if (deletedDevice.current_device) {
          unauthorizeLogout(dispatch);
          navigate('/');
        } else {
          queryClient.invalidateQueries('authDevices');
        }
        hideConfirmationModal();
        Toast_Func({
          status: true,
          message: 'Device removed successfully.',
        });
      })
      .catch((error) => {
        Toast_Func({
          status: false,
          message: error.response.data.message,
        });
      });
  };

  const handleSendOtp = async () => {
    setIsSendingOtp(true);
    try {
      const response = await axiosWithInterceptor.post(
        `${CUSTOMER_MICRO_SERVICE}/send-otp`,
        { is_authenticated_user: true, is_catering: true },
      );
      setOtpExpiryTime(response.data.data.expires_at);
      setEmail(response.data.data.email);
      setOtpModalDescription(response.data.data.popup_description);
      setShowConfirmationModal(false);
      setDisplayOtpModal(true);
      Toast_Func({
        status: true,
        message: response?.data?.message,
      });
      return true;
    } catch (error) {
      if (Boolean(error?.response?.data?.otp_popup)) {
        setOtpExpiryTime(error?.response?.data?.data?.expires_at);
        setEmail(error?.response?.data?.data?.email);
        setShowConfirmationModal(false);
        setDisplayOtpModal(Boolean(error?.response?.data?.otp_popup));
        setOtpModalDescription(error?.response?.data.data.popup_description);
      }
      Toast_Func({
        status: false,
        message:
          error?.response?.data?.message || error?.response?.data?.errors?.[0],
      });
      return false;
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleResendOtp = async (setSubmitting) => {
    setSubmitting(true);
    const isOtpSent = await handleSendOtp();
    setSubmitting(false);
  };

  const handleRemoveAllDevice = () => {
    setConfirmationModalText(
      'You’re requesting to Logout from all Devices. Would you like to continue?',
    );
    setShowConfirmationModal(true);
  };

  const handleRemoveDevice = (deviceToken: string) => {
    setConfirmationModalText(
      'You’re requesting to logout of a Device. Would you like to continue?',
    );
    setShowConfirmationModal(true);
    setDeviceToken(deviceToken);
  };

  const handleModalConfirmation = (setSubmitting = null) => {
    if (deviceToken && !setSubmitting) {
      removeSingleDevice();
    } else {
      handleSendOtp();
    }
  };

  if (isLoading) {
    return <Loader className={styles.recentOrderSpinner} />;
  }
  return (
    <>
      <button
        onClick={(event: any) => {
          event.preventDefault();
          goToRoute(ROUTE_PROFILE_DETAILS);
        }}
        className="btn f-s16  ms-md-3 mt-5 d-flex back-arrow-btn align-items-center gap-2"
      >
        Back to Account Details
      </button>
      <div className="container py-5">
        <button
          onClick={() => handleRemoveAllDevice()}
          type="button"
          className="btn btn-large red d-block m-auto mb-5"
        >
          Logout from all Devices
        </button>
        <h4 className="font-Cls f-sm-s22 f-s24 text-start h-24 medium_text mb-4 ">
          Other Authenticated Devices
        </h4>
        {allDevices.map((device) => (
          <>
            <AuthenticatedDeviceCard
              device={device}
              handleRemoveDevice={handleRemoveDevice}
            />
          </>
        ))}
      </div>
      <CustomModal
        showModal={showConfirmationModal}
        title={'Remove Devices'}
        closeModal={() => hideConfirmationModal()}
        modalBodyClass="text-center"
        modalDialogClasses={`${styles.taxExemptionModal} ${styles.removePaymentModal}`}
        headerClasss="justify-content-center pt-5"
      >
        <ConfirmationModal
          closeModal={() => hideConfirmationModal()}
          description={confirmationModalText}
          buttonText="remove"
          handleClick={(e) => {
            handleModalConfirmation();
          }}
          loading={isSendingOtp || removingDevice}
        />
      </CustomModal>
      <OtpVerificationModal
        displayOtpModal={displayOtpModal}
        setDisplayOtpModal={setDisplayOtpModal}
        handleOtpVerification={removeAllDevices}
        resendOtp={handleResendOtp}
        userEmail={email}
        otpExpiry={otpExpiryTime}
        otpModalDescription={otpModalDescription}
      />
    </>
  );
};

export default MoreDevices;
